import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { QcChecklistCheck, QcChecklistItem } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { loadQcExample, loadQcObservationPhotos, loadQcPassPhoto } from "@/lib/manufacturing/qcChecklistPhotoRead";
import { QC_FAIL_PHOTO_CAP, type QcQueuedPhoto } from "@/lib/manufacturing/qcPhotoQueue";
import { QcStoredPhotoLink } from "@/components/manufacturing/qc/QcStoredPhotoLink";
import { QcLocalPhotoRows } from "./QcLocalPhotoRows";

export function BuilderAssistantChecklistItemRow({
  sessionId,
  item,
  check,
  needsPhoto,
  notesRequired,
  note,
  savedFailureCount,
  localPhotos,
  disabled,
  onNote,
  onPass,
  onFail,
  onNa,
  onFailPhoto,
  onPassPhoto,
  onRemoveLocalPhoto,
  onRetryLocalPhoto,
}: {
  sessionId: string;
  item: QcChecklistItem;
  check: QcChecklistCheck | undefined;
  needsPhoto: boolean;
  notesRequired: boolean;
  note: string;
  savedFailureCount: number;
  localPhotos: readonly QcQueuedPhoto[];
  disabled: boolean;
  onNote: (value: string) => void;
  onPass: () => void;
  onFail: () => void;
  onNa: () => void;
  onFailPhoto: (file: File) => void;
  onPassPhoto: (file: File) => void;
  onRemoveLocalPhoto: (id: string) => void;
  onRetryLocalPhoto: (id: string) => void;
}) {
  const chosenClass =
    "rounded-lg border border-blue-600 bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60";
  const localFail = localPhotos.filter((photo) => photo.kind === "fail");
  const localPass = localPhotos.filter((photo) => photo.kind === "pass");
  const photoCount = savedFailureCount + localFail.length;
  const missing = check?.result === "fail" && needsPhoto && photoCount === 0;
  const hasExample = item.has_example || Boolean(item.example_url);
  const hasPass = check?.has_pass || Boolean(check?.pass_url);
  return (
    <li className="min-w-0 space-y-2 overflow-hidden rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">{item.title}</p>
          {item.description ? <p className="text-xs text-gray-600 dark:text-zinc-400">{item.description}</p> : null}
          {item.status === "tentative" ? <p className="text-xs text-amber-800 dark:text-amber-200">Pending owner</p> : null}
        </div>
        {hasExample ? <QcStoredPhotoLink label="Example" load={() => loadQcExample(item.id)} /> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={check?.result === "pass" ? chosenClass : ghostBtnClass} disabled={disabled} onClick={onPass}>
          {check?.result === "pass" ? "Passed" : "Pass"}
        </button>
        <button type="button" className={check?.result === "fail" ? chosenClass : ghostBtnClass} disabled={disabled} onClick={onFail}>
          {check?.result === "fail" ? "Failed" : "Fail"}
        </button>
        <button type="button" className={check?.result === "na" ? chosenClass : ghostBtnClass} disabled={disabled} onClick={onNa}>
          {check?.result === "na" ? "Not applicable" : "N/A"}
        </button>
      </div>
      {check?.result === "pass" ? (
        <div className="flex flex-wrap items-center gap-2">
          <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
            Pass photo
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              disabled={disabled || localPass.length >= 2}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) onPassPhoto(file);
              }}
            />
          </label>
          {hasPass ? <QcStoredPhotoLink label="View pass photo" load={() => loadQcPassPhoto(sessionId, item.id)} /> : null}
          <QcLocalPhotoRows photos={localPass} savedCount={0} disabled={disabled} onRemove={onRemoveLocalPhoto} onRetry={onRetryLocalPhoto} />
        </div>
      ) : null}
      {check?.result === "fail" ? (
        <div className="space-y-2">
          {photoCount < QC_FAIL_PHOTO_CAP ? (
            <label className={`${ghostBtnClass} inline-flex cursor-pointer${missing ? " ring-2 ring-red-500" : ""}`}>
              {needsPhoto ? "Failure photo" : "Optional failure photo"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                disabled={disabled}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) onFailPhoto(file);
                }}
              />
            </label>
          ) : (
            <span className="text-sm text-gray-900 dark:text-zinc-100">3 photos</span>
          )}
          {savedFailureCount > 0 && check.observation_id ? (
            <QcStoredPhotoLink
              label={`Failure images (${savedFailureCount})`}
              load={async () => {
                const listed = await loadQcObservationPhotos(check.observation_id ?? "", "failure");
                if (!listed.ok) return listed;
                return { ok: true, urls: listed.data.photos.map((photo) => photo.url) };
              }}
            />
          ) : null}
          <QcLocalPhotoRows
            photos={localFail}
            savedCount={savedFailureCount}
            disabled={disabled}
            onRemove={onRemoveLocalPhoto}
            onRetry={onRetryLocalPhoto}
          />
        </div>
      ) : null}
      <textarea
        className={`${inputClass} min-h-14 w-full`}
        placeholder={check?.result === "fail" && notesRequired ? "Describe what failed" : "If it failed, describe what you see"}
        value={note}
        disabled={disabled}
        onChange={(event) => onNote(event.target.value)}
      />
    </li>
  );
}
